import ApiBase from '../apiBase';
import CacheService from '../../services/CacheService';

const pageSize = 9999999;

// Cache anahtarları için sabitler
const CACHE_KEYS = {
	PINS: 'pins',
	LOCATIONS: 'locations',
	ACTOR_CATEGORIES: 'actorCategories',
	EVENT_TYPES: 'eventTypes',
	QUESTIONS: (categoryId?: string) => `questions_${categoryId || 'all'}`,
	QUESTION_CATEGORIES: 'questionCategories',
	PIN_INFO: (pinId: string) => `pin_info_${pinId}`,
	PIN_EVENTS: (pinId: string, startDate: string, endDate: string) =>
		`pin_events_${pinId}_${startDate}_${endDate}`,
	QUESTION_ANSWERS: (pinId: string) => `question_answers_${pinId}`,
	CATEGORY_RISK_ANSWERS: (pinId: string) => `category_risk_answers_${pinId}`,
	PIN_REPORT: (pinId: string) => `pin_report_${pinId}`,
	PIN_FULL_REPORT: (pinId: string) => `pin_full_report_${pinId}`,
	PIN_QUESTIONS_AND_ANSWERS: (pinId: string) => `pin_questions_and_answers_${pinId}`,
};

// Expiry sürelerini milisaniye cinsinden tanımla
const CACHE_EXPIRY = {
	SHORT: 5 * 60 * 1000, // 5 dakika
	MEDIUM: 30 * 60 * 1000, // 30 dakika
	LONG: 12 * 60 * 60 * 1000, // 12 saat
	VERY_LONG: 24 * 60 * 60 * 1000, // 24 saat
};

export interface PinData {
	Name: string;
	Latitude: number;
	Longitude: number;
	RadiusInMeters: number;
	Sector: string;
	userId: string;
	organizationId: string;
	File?: File;
}

interface PinResponse {
	id: string;
	userId: string;
	organizationId: string;
	name: string;
	latitude: number;
	longitude: number;
	radiusInMeters: number;
	locationCount: number;
	sector: string;
	reportDescription: string | null;
	districtDistributionJson: string;
}

interface PinQuestionAnswer {
	questionId: string;
	userId: string;
	yesNo: boolean;
	findings: string;
	pinId: string;
	proposedSolutions: string;
	description: string;
	files?: File[];
	fileAttachmentIdsToRemove?: string[];
}

interface QuestionCategoryCreate {
	name: string;
	userId: string;
	questionTypeId: string;
}

interface QuestionCreate {
	text: string;
	userId: string;
	questionCategoryId: string;
	pinId: string;
}

export interface PinInfo {
	Id: string;
	UserId: string;
	Name: string;
	Latitude: number;
	Longitude: number;
	RadiusInMeters: number;
	LocationCount: number;
	Sector: string;
	ReportDescription: string | null;
	DistrictDistributionJson: string;
	QuestionsJson: {
		wikipediaTitle: string;
		wikipediaSummary: string;
	};
	JsonContentStream: any;
	Locations: any[];
	fileAttachments?: Array<{
		id: string;
		fileName: string;
	}>;
}

interface PinEventsResponse {
	locations: {
		locationId: string;
		district1: string;
		events: {
			id: string;
			date: string;
			eventType: string;
			description: string;
		}[];
	}[];
}

interface CategoryRiskAnswer {
	questionCategoryId: string;
	pinId: string;
	userId: string;
	probabilityLevel: number;
	impactLevel: number;
	threatLevel: number;
	appliedMeasureCoefficient: number;
	residualRiskLevel: number;
	isAfterMeasures: boolean;
}

interface CategoryRiskAnswerUpdate extends CategoryRiskAnswer {
	id: string;
}

interface TranscriptionResponse {
	transcription: string;
}

interface PinReportResponse {
	pinId: string;
	reportDescription: string;
	filteredEventCount: number;
	filteredLocationCount: number;
}


interface PinCategoryRiskAnswer {
	id: string;
	probabilityLevel: number;
	impactLevel: number;
	threatLevel: number;
	appliedMeasureCoefficient: number;
	residualRiskLevel: number;
	isAfterMeasures: boolean;
	categoryName: string;
	questionTypeName: string;
}

interface PinQuestionsResponse {
	pin: {
		id: string;
		name: string;
		latitude: number;
		longitude: number;
		radiusInMeters: number;
		sector: string;
		reportDescription: string | null;
		fileAttachments: {
			id: string;
			fileName: string;
		}[];
		questionAnswers: {
			id: string;
			questionId: string;
			questionText: string;
			questionCategoryName: string;
			description: string | null;
			findings: string;
			proposedSolutions: string;
			fileAttachments: {
				id: string;
				fileName: string;
			}[];
		}[];
		categoryRiskAnswers: PinCategoryRiskAnswer[];
	}
}

interface PinFullReportData {
	pinInfo: {
		Name: string;
		Sector: string;
		Latitude: number;
		Longitude: number;
		RadiusInMeters: number;
		reportDescription: string | null;
		fileAttachments: {
			id: string;
			fileName: string;
		}[];
		Id: string;
	};
	questionAnswers: {
		answer: {
			id: string;
			questionId: string;
			findings: string;
			proposedSolutions: string;
			description: string | null;
			questionCategoryName: string;
			fileAttachments: {
				id: string;
				fileName: string;
			}[];
		};
	}[];
	riskAnswers: {
		id: string;
		probabilityLevel: number;
		impactLevel: number;
		threatLevel: number;
		appliedMeasureCoefficient: number;
		residualRiskLevel: number;
		isAfterMeasures: boolean;
		categoryName: string;
		questionTypeName: string;
	}[];
}

type PinsResponseType = {
	items: PinResponse[];
	count: number;
	index: number;
	size: number;
	hasNext: boolean;
	hasPrevious: boolean;
};

interface ChangePasswordRequest {
	email: string;
	newPassword: string;
}

interface VerifyCodeRequest {
	email: string;
	code: string;
	validationType: number;
}

interface VerifyCodeResponse {
	success: boolean;
	isSuccess: boolean;
	isVerified: boolean;
	message?: string;
}

interface UserResponse {
	id: string;
	firstName: string;
	lastName: string;
	email: string;
	status: boolean;
	organizationId: string;
}

interface QuestionType {
	id: string;
	name: string;
}

interface QuestionCategory {
	id: string;
	name: string;
	isDisabled: boolean;
	index: number;
	questionType: QuestionType;
}

export interface PinQuestionDetailsAnswer {
	id: string;
	yesNo: boolean;
	description: string | null;
	findings: string;
	proposedSolutions: string;
	fileAttachments: {
		id: string;
		fileName: string;
	}[];
	pinId: string;
}

export interface PinQuestion {
	id: string;
	text: string;
	isDisabled: boolean;
	userId: string | null;
	index: number;
	questionCategory: QuestionCategory;
	answers: PinQuestionDetailsAnswer[];
}

interface PinQuestionsAndAnswersResponse {
	questions: PinQuestion[];
}

interface SendEmailValidationRequest {
	email: string;
}

interface SendEmailValidationResponse {
	success: boolean;
	message?: string;
}

interface GenerateInsightRequest {
	question: string;
	tespit: string;
}

interface GenerateInsightResponse {
	insight: string;
}

const getPinReport = async (pinId: string, startDate: string, endDate: string): Promise<PinReportResponse> => {
	const cacheKey = CACHE_KEYS.PIN_REPORT(pinId);

	return CacheService.getOrFetch<PinReportResponse>(
		cacheKey,
		async () => {
			return ApiBase.postReportPromise<PinReportResponse>(
				`/api/Pins/${pinId}/createReport?startDate=${startDate}&endDate=${endDate}`
			);
		},
		CACHE_EXPIRY.MEDIUM
	);
};

const getPinFullReport = async (pinId: string): Promise<PinFullReportData> => {
	const cacheKey = CACHE_KEYS.PIN_FULL_REPORT(pinId);

	return CacheService.getOrFetch<PinFullReportData>(
		cacheKey,
		async () => {
			try {
				const response = (await ApiBase.getPromise<PinQuestionsResponse>(`/api/Pins/Questions?pinId=${pinId}`)) as PinQuestionsResponse;
				const pin = response.pin;

				return {
					pinInfo: {
						Name: pin.name,
						Sector: pin.sector,
						Latitude: pin.latitude,
						Longitude: pin.longitude,
						RadiusInMeters: pin.radiusInMeters,
						reportDescription: pin.reportDescription,
						fileAttachments: pin.fileAttachments,
						Id: pin.id
					},
					questionAnswers: pin.questionAnswers.map(qa => ({
						answer: {
							id: qa.id,
							questionId: qa.questionId,
							findings: qa.findings,
							proposedSolutions: qa.proposedSolutions,
							description: qa.description,
							questionCategoryName: qa.questionCategoryName,
							fileAttachments: qa.fileAttachments
						}
					})),
					riskAnswers: pin.categoryRiskAnswers.map(ra => ({
						id: ra.id,
						probabilityLevel: ra.probabilityLevel,
						impactLevel: ra.impactLevel,
						threatLevel: ra.threatLevel,
						appliedMeasureCoefficient: ra.appliedMeasureCoefficient,
						residualRiskLevel: ra.residualRiskLevel,
						isAfterMeasures: ra.isAfterMeasures,
						categoryName: ra.categoryName,
						questionTypeName: ra.questionTypeName
					}))
				};
			} catch (error) {
				console.error('getPinFullReport error:', error);
				throw error;
			}
		},
		CACHE_EXPIRY.MEDIUM
	);
};

const getFileAttachment = async (fileAttachmentId: string): Promise<Blob> => {
	try {
		return await ApiBase.getBlobPromise(
			`/api/FileAttachments/${fileAttachmentId}/download`
		);
	} catch (error) {
		throw error;
	}
};

const getPinQuestionsAndAnswers = async (pinId: string): Promise<PinQuestionsAndAnswersResponse> => {
	try {
		const response = await ApiBase.getPromise<PinQuestionsAndAnswersResponse>(
			`/api/Pins/questions/details?pinId=${pinId}`
		);
		return response as PinQuestionsAndAnswersResponse;
	} catch (error) {
		console.error('getPinQuestionsAndAnswers error:', error);
		throw error;
	}
};

const EventsApi = {
	// Tüm önbelleği temizle
	clearCache() {
		CacheService.clearAll();
	},

	// Belirli bir öğenin önbelleğini temizle
	clearCacheItem(key: string) {
		CacheService.clear(key);
	},

	getEvents(params?: any) {
		// Events sürekli değiştiği için önbelleğe almıyoruz
		return ApiBase.getPromise('/api/Events', { ...params, pageSize });
	},

	getEventsCounts(params?: any) {
		return ApiBase.getPromise('/api/Events', { ...params });
	},

	getPins() {
		const userId = localStorage.getItem('userId');
		const cacheKey = CACHE_KEYS.PINS;

		return CacheService.getOrFetch<PinsResponseType>(
			cacheKey,
			async () => {
				// First get the user to get their organizationId
				const user = await this.getUser(userId || '');
				const response = await ApiBase.getPromise<PinsResponseType>(`/api/Pins?organizationId=${user.organizationId}`);
				return response as PinsResponseType;
			},
			CACHE_EXPIRY.MEDIUM
		);
	},

	getLocations() {
		return CacheService.getOrFetch(
			CACHE_KEYS.LOCATIONS,
			() => ApiBase.getPromise('/api/Locations', { pageSize }),
			CACHE_EXPIRY.LONG
		);
	},

	getActorCategories() {
		return CacheService.getOrFetch(
			CACHE_KEYS.ACTOR_CATEGORIES,
			() => ApiBase.getPromise('/api/ActorCategories', { pageSize }),
			CACHE_EXPIRY.LONG
		);
	},

	getEventTypes() {
		return CacheService.getOrFetch(
			CACHE_KEYS.EVENT_TYPES,
			() => ApiBase.getPromise('/api/EventTypes', { pageSize }),
			CACHE_EXPIRY.LONG
		);
	},

	createNewPin(pinData: PinData | FormData): Promise<PinResponse> {
		return ApiBase.postPromise<PinResponse>('/api/Pins/UploadQuestionsJson', pinData)
			.then(response => {
				// Pin oluşturulduğunda pin önbelleğini temizle
				CacheService.clear(CACHE_KEYS.PINS);
				return response;
			});
	},

	deletePin(pinId: string) {
		return ApiBase.deletePromise(`/api/Pins/${pinId}`, null)
			.then(response => {
				// Pin silindiğinde pin önbelleğini ve pin ile ilgili diğer önbellekleri temizle
				CacheService.clear(CACHE_KEYS.PINS);
				CacheService.clear(CACHE_KEYS.PIN_INFO(pinId));
				CacheService.clearByPrefix(`pin_events_${pinId}`);
				CacheService.clear(CACHE_KEYS.QUESTION_ANSWERS(pinId));
				CacheService.clear(CACHE_KEYS.CATEGORY_RISK_ANSWERS(pinId));
				CacheService.clear(CACHE_KEYS.PIN_REPORT(pinId));
				CacheService.clear(CACHE_KEYS.PIN_FULL_REPORT(pinId));
				return response;
			});
	},

	getDistrictBounds() {
		return CacheService.getOrFetch(
			'districtBounds',
			() => fetch('/simplified_ilce_sinirlari.json')
				.then((response) => response.json())
				.catch((error) => {
					console.error(error);
				}),
			CACHE_EXPIRY.VERY_LONG  // Bu veri nadiren değişir, uzun süre önbellekte tutulabilir
		);
	},

	saveQuestionAnswers(answer: PinQuestionAnswer) {
		const formData = new FormData();
		formData.append('QuestionId', answer.questionId || '');
		formData.append('UserId', answer.userId || '');
		formData.append('YesNo', answer.yesNo ? 'true' : 'false');
		formData.append('Findings', answer.findings || '');
		formData.append('PinId', answer.pinId || '');
		formData.append('ProposedSolutions', answer.proposedSolutions || '');
		formData.append('Description', answer.description || '');

		if (answer.files && answer.files.length > 0) {
			answer.files.forEach((file, index) => {
				formData.append(`Files`, file);
			});
		}

		// Add files to remove
		if (answer.fileAttachmentIdsToRemove && answer.fileAttachmentIdsToRemove.length > 0) {
			answer.fileAttachmentIdsToRemove.forEach(fileId => {
				formData.append('FileAttachmentIdsToRemove', fileId);
			});
		}

		console.log('Saving question answer with files:', {
			newFiles: answer.files?.map(f => f.name) || [],
			filesToRemove: answer.fileAttachmentIdsToRemove || []
		});

		return ApiBase.postPromise('/api/QuestionAnswers', formData)
			.then(response => {
				CacheService.clear(CACHE_KEYS.PIN_FULL_REPORT(answer.pinId));
				return response;
			})
			.catch(error => {
				console.error('Error saving question answer:', error);
				// Cloud-specific error handling
				if (error.message?.includes('Network Error') || error.message?.includes('timeout')) {
					console.error('Network error - possible cloud connectivity issue');
				}
				throw error;
			});
	},

	saveQuestionCategory(categoryData: QuestionCategoryCreate) {
		return ApiBase.postQuestionPromise('/api/QuestionCategories', categoryData)
			.then(response => {
				CacheService.clear(CACHE_KEYS.PIN_FULL_REPORT(categoryData.questionTypeId));
				return response;
			});
	},

	saveQuestion(questionData: QuestionCreate) {
		return ApiBase.postQuestionPromise('/api/Questions', questionData)
			.then(response => {
				CacheService.clear(CACHE_KEYS.PIN_FULL_REPORT(questionData.pinId));
				return response;
			});
	},

	getPinInfo(pinId: string): Promise<PinInfo> {
		return CacheService.getOrFetch<PinInfo>(
			CACHE_KEYS.PIN_INFO(pinId),
			() => ApiBase.getPromise(`/api/Pins/${pinId}`) as Promise<PinInfo>,
			CACHE_EXPIRY.MEDIUM
		);
	},

	getPinEvents(pinId: string, startDate: string, endDate: string): Promise<PinEventsResponse> {
		const cacheKey = CACHE_KEYS.PIN_EVENTS(pinId, startDate, endDate);

		return CacheService.getOrFetch<PinEventsResponse>(
			cacheKey,
			() => ApiBase.getPromise(
				`/api/Pins/${pinId}/info?startDate=${startDate}&endDate=${endDate}`
			) as Promise<PinEventsResponse>,
			CACHE_EXPIRY.MEDIUM
		);
	},

	updateQuestionAnswer(formData: FormData) {
		return ApiBase.putPromise('/api/QuestionAnswers', formData)
			.then(response => {
				// Clear relevant caches after update
				const pinId = formData.get('PinId') as string;
				if (pinId) {
					CacheService.clear(CACHE_KEYS.QUESTION_ANSWERS(pinId));
					CacheService.clear(CACHE_KEYS.PIN_FULL_REPORT(pinId));
				}
				return response;
			})
			.catch(error => {
				console.error('Error updating question answer:', error);
				throw error;
			});
	},

	saveCategoryRiskAnswers(data: CategoryRiskAnswer) {
		return ApiBase.postQuestionPromise('/api/CategoryRiskAnswers', data)
			.then(response => {
				// Risk cevabı kaydedildiğinde ilgili önbellekleri temizle
				CacheService.clear(CACHE_KEYS.CATEGORY_RISK_ANSWERS(data.pinId));
				CacheService.clear(CACHE_KEYS.PIN_FULL_REPORT(data.pinId));
				return response;
			});
	},

	updateCategoryRiskAnswers(data: CategoryRiskAnswerUpdate) {
		return ApiBase.putCategoryRiskAnswerPromise('/api/CategoryRiskAnswers', data)
			.then(response => {
				// Risk cevabı güncellendiğinde ilgili önbellekleri temizle
				CacheService.clear(CACHE_KEYS.CATEGORY_RISK_ANSWERS(data.pinId));
				CacheService.clear(CACHE_KEYS.PIN_FULL_REPORT(data.pinId));
				return response;
			});
	},

	getCategoryRiskAnswers(pinId: string) {
		return CacheService.getOrFetch(
			CACHE_KEYS.CATEGORY_RISK_ANSWERS(pinId),
			() => ApiBase.getPromise(`/api/CategoryRiskAnswers?pinId=${pinId}`, { pageSize: 500 }),
			CACHE_EXPIRY.MEDIUM
		);
	},

	getSpeechToText(audioBlob: Blob) {
		const formData = new FormData();
		formData.append('audioFile', audioBlob, 'audio.webm');

		return ApiBase.postPromise<TranscriptionResponse>('/api/SpeechToText/transcribe', formData);
	},

	getPinReport,
	getPinFullReport,

	updatePin(pinId: string, formData: FormData) {
		// Log the pinId and iterate over formData entries
		console.log('Updating Pin:', pinId);
		formData.forEach((value, key) => {
			console.log(`${key}: ${value}`);
		});
		return ApiBase.putPromise(`/api/Pins`, formData)
			.then(response => {
				// Pin güncellendiğinde ilgili önbellekleri temizle
				CacheService.clear(CACHE_KEYS.PINS);
				CacheService.clear(CACHE_KEYS.PIN_INFO(pinId));
				CacheService.clear(CACHE_KEYS.PIN_FULL_REPORT(pinId));
				CacheService.clear(CACHE_KEYS.PIN_REPORT(pinId));
				return response;
			});
	},

	getFileAttachment,

	changePassword(data: ChangePasswordRequest) {
		return ApiBase.postQuestionPromise('/api/Auth/reset-password', data);
	},

	verifyCode(data: VerifyCodeRequest): Promise<VerifyCodeResponse> {
		const verifyData = {
			...data,
			validationType: 2 // Sabit validation type
		};

		return ApiBase.postJsonPromise<VerifyCodeResponse>('/api/Auth/verify-code', verifyData);
	},

	getUser(userId: string): Promise<UserResponse> {
		const cacheKey = `user_${userId}`;

		return CacheService.getOrFetch<UserResponse>(
			cacheKey,
			async () => {
				const response = await ApiBase.getPromise<UserResponse>(`/api/Users/${userId}`);
				return response as UserResponse;
			},
			CACHE_EXPIRY.MEDIUM
		);
	},

	// Önbelleği zorla yenileme metodları
	refreshPins() {
		return CacheService.refresh(CACHE_KEYS.PINS, async () => {
			const userId = localStorage.getItem('userId');
			const user = await this.getUser(userId || '');
			const response = await ApiBase.getPromise<PinsResponseType>(`/api/Pins?organizationId=${user.organizationId}`);
			return response as PinsResponseType;
		});
	},

	refreshPinFullReport(pinId: string) {
		return CacheService.refresh(CACHE_KEYS.PIN_FULL_REPORT(pinId), () => getPinFullReport(pinId));
	},

	refreshQuestionCategories() {
		return CacheService.refresh(CACHE_KEYS.QUESTION_CATEGORIES, () =>
			ApiBase.getPromise('/api/QuestionCategories', { pageSize: 500 })
		);
	},

	getPinQuestionsAndAnswers,

	sendPasswordReset(email: string) {
		const data = { email };
		return ApiBase.postJsonPromise('/api/Auth/send-password-reset', data);
	},

	resetPassword(data: { email: string; code: string; newPassword: string }) {
		return ApiBase.postJsonPromise('/api/Auth/reset-password', data);
	},

	sendEmailValidation(data: SendEmailValidationRequest): Promise<SendEmailValidationResponse> {
		return ApiBase.postJsonPromise<SendEmailValidationResponse>('/api/Auth/send-email-validation', data);
	},

	verifyEmailCode(data: VerifyCodeRequest): Promise<VerifyCodeResponse> {
		const verifyData = {
			...data,
			validationType: 2 // Email validation type
		};

		return ApiBase.postJsonPromise<VerifyCodeResponse>('/api/Auth/verify-code', verifyData);
	},

	generateInsight(data: GenerateInsightRequest): Promise<GenerateInsightResponse> {
		return ApiBase.postJsonPromise<GenerateInsightResponse>('/api/QuestionAnswers/generate-insight', data);
	},
};

export default EventsApi;
